import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import { colorWithAlpha, parseColor } from '../utils/colorValue';
import './ColorInput.css';

const displayHex = value => {
    if (!value) return '';
    const color = parseColor(value);
    return color ? colorWithAlpha(color.hex, color.alpha) : value;
};

// Preserve the native input callback contract used by every existing editor.
export default function ColorInput({ value = '', onChange, style, className = '', id, name }) {
    const parsed = useMemo(() => parseColor(value), [value]);
    const hex = parsed?.hex || '#000000';
    const alpha = parsed?.alpha ?? 1;
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState(() => displayHex(value));
    const [focused, setFocused] = useState(false);
    const dirty = useRef(false);
    const generatedId = useId();
    const inputId = id || generatedId;
    const buttonRef = useRef(null);
    useEffect(() => {
        if (!focused) setDraft(displayHex(value));
    }, [value, focused]);

    const emit = next => {
        if (next === value) return;
        const target = { value: next, id: inputId, name };
        onChange?.({ target, currentTarget: target });
    };
    const commitHex = raw => {
        const text = raw.trim();
        if (!/^#?(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(text)) return false;
        const color = parseColor(text.startsWith('#') ? text : '#' + text);
        const hasAlpha = text.replace(/^#/, '').length === 4 || text.replace(/^#/, '').length === 8;
        emit(colorWithAlpha(color.hex, hasAlpha ? color.alpha : alpha));
        return true;
    };
    return <span className="muulti-color-input" onBlur={event => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
    }} onClick={event => {
        // Avoid activating an enclosing editor label when clicking the visual panel.
        if (!event.target.closest('input, button')) event.preventDefault();
    }} onKeyDown={event => {
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            setOpen(false);
            buttonRef.current?.focus();
        }
    }}>
        <span className="muulti-color-input__row">
            <button ref={buttonRef} type="button" className="muulti-color-input__swatch"
                aria-label="Choose colour" aria-expanded={open} aria-controls={inputId + '-panel'}
                onClick={() => setOpen(current => !current)}>
                <span style={{ backgroundColor: value || 'transparent' }} />
            </button>
            <input id={inputId} name={name} type="text" aria-label="Hex colour"
                className={'muulti-color-input__hex ' + className} style={style}
                value={draft} placeholder="Default" spellCheck={false} autoComplete="off"
                onFocus={() => { dirty.current = false; setFocused(true); }}
                onChange={event => {
                    const raw = event.target.value;
                    dirty.current = true;
                    setDraft(raw);
                    // Short Hex remains a local draft until blur; typing never jumps.
                    if (/^#?(?:[0-9a-f]{6}|[0-9a-f]{8})$/i.test(raw)) commitHex(raw);
                }}
                onBlur={() => {
                    if (dirty.current) commitHex(draft);
                    dirty.current = false;
                    setFocused(false);
                    setDraft(displayHex(value));
                }}
                onKeyDown={event => {
                    if (event.key === 'Enter') {
                        event.preventDefault();
                        event.stopPropagation();
                        event.currentTarget.blur();
                    }
                }} />
        </span>
        {open && <span id={inputId + '-panel'} className="muulti-color-input__panel" role="group" aria-label="Colour selection">
            <HexColorPicker color={hex} onChange={next => emit(colorWithAlpha(next, alpha))} />
            <span className="muulti-color-input__opacity-heading">
                <span>Opacity</span><span>{Math.round(alpha * 100)}%</span>
            </span>
            <input type="range" className="muulti-color-input__opacity" aria-label="Opacity"
                min="0" max="100" step="1" value={Math.round(alpha * 100)}
                style={{ '--opacity-color': hex }}
                onChange={event => emit(colorWithAlpha(hex, Number(event.target.value) / 100))} />
            <span className="muulti-color-input__hint">0% transparent / 100% solid</span>
        </span>}
    </span>;
}
